const PROMO_DATE_TZ = 'Europe/Moscow';

function formatDiscountNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  if (Number.isInteger(value)) return String(value);
  return String(value).replace(/\.?0+$/, '');
}

export function formatPromoPushDiscountValue(
  discountType: string,
  discountValue: number,
): string {
  switch (discountType) {
    case 'percentage':
      return `−${formatDiscountNumber(discountValue)}%`;
    case 'fixed_amount':
      return `−${formatDiscountNumber(discountValue)} ₽`;
    case 'free_delivery':
      return 'бесплатная доставка';
    default:
      return `−${formatDiscountNumber(discountValue)}`;
  }
}

export function formatPromoPushDate(
  value?: Date | string | null,
): string | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PROMO_DATE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  if (!map.day || !map.month || !map.year) return null;
  return `${map.day}.${map.month}.${map.year}`;
}

export function formatPromoPushValidity(params: {
  validFrom?: Date | string | null;
  validUntil?: Date | string | null;
}): string | null {
  const from = formatPromoPushDate(params.validFrom ?? null);
  const until = formatPromoPushDate(params.validUntil ?? null);
  if (from != null && until != null) {
    return `Действует с ${from} до ${until}`;
  }
  if (from != null) {
    return `Действует с ${from}`;
  }
  if (until != null) {
    return `Действует до ${until}`;
  }
  return null;
}

/** Товары имеют приоритет над категорией (как в логике промокода). */
export function formatPromoPushScope(params: {
  categoryName?: string | null;
  productNames?: string[] | null;
}): string | null {
  const products = (params.productNames ?? [])
    .map((n) => n.trim())
    .filter(Boolean);
  if (products.length > 0) {
    return `Скидка на товары: ${products.join(', ')}`;
  }
  const category = params.categoryName?.trim();
  if (category) {
    return `Скидка на товары из категории «${category}»`;
  }
  return null;
}

/** Превью текста пуша — тот же шаблон, что на бэкенде. */
export function buildPromoCodePushMessage(params: {
  code: string;
  discountType: string;
  discountValue: number;
  shopName?: string | null;
  validFrom?: Date | string | null;
  validUntil?: Date | string | null;
  categoryName?: string | null;
  productNames?: string[] | null;
}): { title: string; body: string } {
  const code = (params.code || '').trim().toUpperCase() || 'КОД';
  const value = formatPromoPushDiscountValue(
    params.discountType,
    Number(params.discountValue) || 0,
  );
  const shop = params.shopName?.trim();
  const discountPart = shop
    ? `Скидка ${value} в магазине «${shop}»`
    : `Скидка ${value}`;
  const validity = formatPromoPushValidity({
    validFrom: params.validFrom ?? null,
    validUntil: params.validUntil ?? null,
  });
  const scope = formatPromoPushScope({
    categoryName: params.categoryName ?? null,
    productNames: params.productNames ?? null,
  });
  const parts = [discountPart];
  if (validity) parts.push(validity);
  if (scope) parts.push(scope);
  return {
    title: `Промокод ${code}`,
    body: `${parts.join('. ')}.`,
  };
}
